import React, { useState } from 'react';
import { MessageSquare, Eye, TrendingUp, Lightbulb, BarChart3, RefreshCw } from 'lucide-react';
import { QuestionAnalysis } from '../types';

interface SampleQuestionsProps {
    questions: string[];
    questionAnalysis?: QuestionAnalysis;
    assistantName: string;
    assistantId: string;
    onRefreshAnalysis?: () => Promise<void>;
    newQuestionsCount?: number;
}

// Reusable header component
const AnalysisHeader: React.FC<{
    actionButton?: React.ReactNode;
    progressIndicator?: React.ReactNode;
}> = ({ actionButton, progressIndicator }) => (
    <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-slate-900">What people ask about</h3>
        </div>
        {actionButton}
        {progressIndicator}
    </div>
);

// Reusable empty state component
const EmptyState: React.FC<{
    icon: React.ReactNode;
    message: string;
}> = ({ icon, message }) => (
    <div className="text-center py-8">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {icon}
        </div>
        <p className="text-slate-500 text-sm">{message}</p>
    </div>
);

// Reusable category card component
const CategoryCard: React.FC<{
    category: string;
    count: number;
    examples: string[];
}> = ({ category, count, examples }) => (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-slate-900">{category}</h5>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {count} questions
            </span>
        </div>
        <div className="space-y-2">
            {examples.slice(0, 2).map((example, idx) => (
                <p key={idx} className="text-sm text-slate-600 italic">
                    "{example}"
                </p>
            ))}
            {examples.length > 2 && (
                <p className="text-xs text-slate-500">
                    +{examples.length - 2} more similar questions
                </p>
            )}
        </div>
    </div>
);

// Reusable theme tag component
const ThemeTag: React.FC<{ theme: string }> = ({ theme }) => (
    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
        {theme}
    </span>
);

// Reusable insight item component
const InsightItem: React.FC<{ insight: string }> = ({ insight }) => (
    <li className="text-sm text-blue-700 flex items-start">
        <span className="text-blue-500 mr-2">•</span>
        {insight}
    </li>
);

// Reusable refresh button component
const RefreshButton: React.FC<{
    newQuestionsCount: number;
    isRefreshing: boolean;
    onClick: () => void;
}> = ({ newQuestionsCount, isRefreshing, onClick }) => (
    <button
        onClick={onClick}
        disabled={isRefreshing}
        className="flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title={`Refresh analysis with ${newQuestionsCount} new questions (may incur AI costs)`}
    >
        <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? 'Refreshing...' : `Refresh (${newQuestionsCount} new)`}
    </button>
);

// Reusable progress indicator component
const ProgressIndicator: React.FC<{
    newQuestionsCount: number;
    currentQuestionsCount: number;
}> = ({ newQuestionsCount, currentQuestionsCount }) => (
    <div className="flex items-center px-3 py-2 text-sm text-slate-500 bg-slate-50 rounded-lg">
        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
        {newQuestionsCount} new questions (need {3 - currentQuestionsCount} more for analysis)
    </div>
);

// Reusable insights section component
const InsightsSection: React.FC<{ insights: string[] }> = ({ insights }) => (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                <Lightbulb className="w-3 h-3" />
            </div>
            <div>
                <p className="text-sm text-blue-800 font-medium mb-2">Key Insights</p>
                <ul className="space-y-1">
                    {insights.map((insight, index) => (
                        <InsightItem key={index} insight={insight} />
                    ))}
                </ul>
            </div>
        </div>
    </div>
);

const SampleQuestions: React.FC<SampleQuestionsProps> = ({
    questions,
    questionAnalysis,
    assistantName,
    assistantId,
    onRefreshAnalysis,
    newQuestionsCount = 0
}) => {
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = async () => {
        if (!onRefreshAnalysis) return;

        setIsRefreshing(true);
        try {
            await onRefreshAnalysis();
        } catch (error) {
            console.error('Error refreshing analysis:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Early returns for empty states
    if (!questions || questions.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                <div className="p-6">
                    <AnalysisHeader />
                    <EmptyState
                        icon={<Eye className="w-8 h-8 text-slate-400" />}
                        message={`No questions yet. Analysis will appear here once people start chatting with ${assistantName}.`}
                    />
                </div>
            </div>
        );
    }

    if (!questionAnalysis || questionAnalysis.categories.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                <div className="p-6">
                    <AnalysisHeader />
                    <EmptyState
                        icon={<BarChart3 className="w-8 h-8 text-slate-400" />}
                        message="Analyzing questions... Please check back later for insights."
                    />
                </div>
            </div>
        );
    }

    // Render refresh button or progress indicator
    const renderActionButton = () => {
        if (onRefreshAnalysis && questions.length >= 3 && newQuestionsCount > 0) {
            return <RefreshButton newQuestionsCount={newQuestionsCount} isRefreshing={isRefreshing} onClick={handleRefresh} />;
        }
        return null;
    };

    const renderProgressIndicator = () => {
        if (newQuestionsCount > 0 && questions.length < 3) {
            return <ProgressIndicator newQuestionsCount={newQuestionsCount} currentQuestionsCount={questions.length} />;
        }
        return null;
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
            <div className="p-6">
                <AnalysisHeader
                    actionButton={renderActionButton()}
                    progressIndicator={renderProgressIndicator()}
                />

                {/* Categories */}
                <div className="mb-6">
                    <h4 className="text-base font-semibold text-slate-900 mb-4">Question Categories</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {questionAnalysis.categories.map((category, index) => (
                            <CategoryCard
                                key={index}
                                category={category.category}
                                count={category.count}
                                examples={category.examples}
                            />
                        ))}
                    </div>
                </div>

                {/* Common Themes */}
                {questionAnalysis.commonThemes.length > 0 && (
                    <div className="mb-6">
                        <h4 className="text-base font-semibold text-slate-900 mb-3">Common Themes</h4>
                        <div className="flex flex-wrap gap-2">
                            {questionAnalysis.commonThemes.map((theme, index) => (
                                <ThemeTag key={index} theme={theme} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Insights */}
                {questionAnalysis.insights.length > 0 && (
                    <InsightsSection insights={questionAnalysis.insights} />
                )}
            </div>
        </div>
    );
};

export default SampleQuestions;
